import { Application, Post, SavedJob } from '@libs/models';
import { Pagination, PostSearchFiltersDto } from '@libs/dto';

import { api } from '../common.api';

export const jobseekerPostApi = api.injectEndpoints({
  endpoints: (build) => ({
    getJobseekerPosts: build.query<Pagination<Post>, { page: number; limit: number }>({
      query: ({ page, limit }) => `/post/jobseeker?page=${page}&limit=${limit}`,
      providesTags: (result) =>
        result
          ? [
              'Post',
              ...result.items.map((post) => ({ type: 'Post' as const, id: post.id })),
              { type: 'Post', id: 'LIST' },
            ]
          : ['Post', { type: 'Post', id: 'LIST' }],
    }),
    getJobseekerPost: build.query<Post, { id: number }>({
      query: ({ id }) => `/post/jobseeker/${id}`,
      providesTags: (result, error, { id }) => [{ type: 'Post', id }],
    }),
    searchJobseekerPosts: build.query<Pagination<Post>, { page: number; limit: number; filters?: PostSearchFiltersDto }>(
      {
        query: ({ page, limit, filters }) => {
          let queryString = `?page=${page}&limit=${limit}`;

          if (filters?.keyword) {
            queryString += `&keyword=${filters?.keyword}`;
          }

          if (filters?.location) {
            queryString += `&location=${filters?.location}`;
          }

          if (filters?.radius) {
            queryString += `&radius=${filters?.radius}`;
          }

          if (filters?.compensationMin) {
            queryString += `&compensationMin=${filters?.compensationMin}`;
          }

          if (filters?.compensationMax) {
            queryString += `&compensationMax=${filters?.compensationMax}`;
          }

          if (filters?.compensationType) {
            queryString += `&compensationType=${filters?.compensationType}`;
          }

          if (filters?.commitmentType) {
            queryString += `&commitmentType=${filters?.commitmentType}`;
          }

          if (filters?.benefits) {
            queryString += `&benefits=${filters?.benefits}`;
          }

          if (filters?.startDate) {
            queryString += `&startDate=${filters?.startDate}`;
          }

          return `/post/jobseeker/search${queryString}`;
        },
        providesTags: (result) =>
          result
            ? [
                'Post',
                ...result.items.map((post) => ({ type: 'Post' as const, id: post.id })),
                { type: 'Post', id: 'LIST' },
              ]
            : ['Post', { type: 'Post', id: 'LIST' }],
      },
    ),
    getJobseekerApplications: build.query<Pagination<Application>, { page: number; limit: number }>({
      query: ({ page, limit }) => `/post/jobseeker/applications?page=${page}&limit=${limit}`,
      providesTags: ['Application'],
    }),
    getJobseekerApplicationsRecent: build.query<Pagination<Application>, void>({
      query: () => `/post/jobseeker/applications/recent`,
      providesTags: ['Application'],
    }),
    getJobseekerSavedPosts: build.query<Pagination<SavedJob>, { page: number; limit: number }>({
      query: ({ page, limit }) => `/post/jobseeker/saved?page=${page}&limit=${limit}`,
      providesTags: ['Jobseeker'],
    }),
    getJobseekerSavedPostsRecent: build.query<Pagination<SavedJob>, void>({
      query: () => `/post/jobseeker/saved/recent`,
      providesTags: ['Jobseeker'],
    }),
  }),
});

export const {
  useLazyGetJobseekerPostsQuery,
  useGetJobseekerPostQuery,
  useLazySearchJobseekerPostsQuery,
  useGetJobseekerApplicationsQuery,
  useGetJobseekerApplicationsRecentQuery,
  useGetJobseekerSavedPostsQuery,
  useGetJobseekerSavedPostsRecentQuery,
} = jobseekerPostApi;
